import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";
import "../../styles/components/ContactSection.css";

import arrowCircle from "../../img/hero/arrow-circle-2.png";
import IconPng from "../../components/IconPng";

import mailIcon from "../../img/contact/mail.png";
import phoneIcon from "../../img/contact/phone.png";
import pinIcon from "../../img/contact/pin.png";
import contactArt from "../../img/hero/contact-art.webp";

// ==== ENV (Vite -> CRA fallback) ====
const SERVICE_ID =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_EMAILJS_SERVICE_ID) ||
  process.env.REACT_APP_EMAILJS_SERVICE_ID;

const TEMPLATE_ID =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_EMAILJS_TEMPLATE_ID) ||
  process.env.REACT_APP_EMAILJS_TEMPLATE_ID;

const PUBLIC_KEY =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_EMAILJS_PUBLIC_KEY) ||
  process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function ContactSection() {
  const formRef = useRef(null);
  const [status, setStatus] = useState({ type: "idle", msg: "" });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validate = () => {
    const f = formRef.current;
    const data = {
      user_name: f.user_name.value.trim(),
      user_email: f.user_email.value.trim(),
      company: f.company.value.trim(),
      message: f.message.value.trim(),
    };

    const newErr = {};
    if (!data.user_name) newErr.user_name = "Required";
    if (!data.user_email) newErr.user_email = "Required";
    else {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!re.test(data.user_email)) newErr.user_email = "Invalid email";
    }
    if (!data.company) newErr.company = "Required";
    if (!data.message) newErr.message = "Required";

    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // honeypot
    const hp = formRef.current?.elements?.hp_field?.value?.trim();
    if (hp) return;

    if (!validate()) {
      setStatus({ type: "error", msg: "Please fill in all fields correctly." });
      return;
    }

    setStatus({ type: "loading", msg: "Sending..." });

    try {
      await emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, formRef.current, { publicKey: PUBLIC_KEY });
      formRef.current.reset();
      setErrors({});
      // ✅ редирект на сторінку подяки
      navigate("/thank-you", { replace: true });
    } catch (err) {
      const msg = err?.text || err?.message || JSON.stringify(err);
      console.error("EmailJS error:", msg);
      setStatus({ type: "error", msg: `EmailJS error: ${msg}` });
    } finally {
      // зняти "loading" у разі помилки
      setStatus((s) => (s.type === "loading" ? { type: "idle", msg: "" } : s));
    }
  };

  const fieldErr = (name) => Boolean(errors[name]);

  return (
    <section className="contact-sec" id="contacts" aria-label="Contacts">
      {/* Арт */}
      <img src={contactArt} alt="" className="contact-sec__art" aria-hidden="true" />

      <div className="contact-sec__cards">
        {/* ===== Ліва картка: інформація ===== */}
        <div className="contact-card contact-card--info contact-card--from-left">
          <h3 className="contact-title">Contact Information</h3>

          <ul className="contact-list" aria-label="Company contacts">
            <li>
              <img className="contact-ico" src={mailIcon} alt="" aria-hidden="true" />
              <a href="mailto:yv@intwriting.com">yv@intwriting.com</a>
            </li>
            <li>
              <img className="contact-ico" src={phoneIcon} alt="" aria-hidden="true" />
              <a href="tel:+48572304192">+48572304192</a>
            </li>
            <li>
              <img className="contact-ico" src={pinIcon} alt="" aria-hidden="true" />
              <span>Chmielna 73, 00-801 Warszawa</span>
            </li>
          </ul>

          <div className="contact-social" aria-label="Social links">
            <a href="https://x.com/your" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
              <IconPng name="X" size={40} />
            </a>
            <a href="https://t.me/your" target="_blank" rel="noopener noreferrer" aria-label="Telegram">
              <IconPng name="Telegram" size={40} />
            </a>
            <a
              href="https://www.linkedin.com/company/intelligent-writing/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <IconPng name="LinkedIn" size={40} />
            </a>
          </div>
        </div>

        {/* ===== Права картка: форма ===== */}
        <div className="contact-card form-card contact-card--from-right">
          <h3 className="contact-title">Fill the form and we will contact you</h3>

          <form ref={formRef} className="contact-form" onSubmit={handleSubmit} noValidate>
            {/* honeypot */}
            <input type="text" name="hp_field" tabIndex="-1" autoComplete="off" style={{ display: "none" }} />

            {/* 1 ряд – 2 колонки */}
            <div className="form-row form-row--2">
              <div className={`form-field ${fieldErr("user_name") ? "has-error" : ""}`}>
                <label className="form-label" htmlFor="name">Your name</label>
                <input
                  id="name"
                  name="user_name"
                  type="text"
                  placeholder="Full Name"
                  aria-invalid={fieldErr("user_name")}
                />
              </div>

              <div className={`form-field ${fieldErr("user_email") ? "has-error" : ""}`}>
                <label className="form-label" htmlFor="email">E-mail</label>
                <input
                  id="email"
                  name="user_email"
                  type="email"
                  placeholder="example@gmail.com"
                  aria-invalid={fieldErr("user_email")}
                />
              </div>
            </div>

            {/* Company — обовʼязкове */}
            <div className={`form-field ${fieldErr("company") ? "has-error" : ""}`}>
              <label className="form-label" htmlFor="company">Company / Organization</label>
              <input
                id="company"
                name="company"
                type="text"
                placeholder="my company"
                aria-invalid={fieldErr("company")}
              />
            </div>

            {/* Message */}
            <div className={`form-field ${fieldErr("message") ? "has-error" : ""}`}>
              <label className="form-label" htmlFor="message">Your message or project description</label>
              <textarea
                id="message"
                name="message"
                placeholder="My message"
                aria-invalid={fieldErr("message")}
              />
            </div>

            {/* Button */}
            <button
              className="Primary v2 contact-form__cta"
              type="submit"
              disabled={status.type === "loading"}
              aria-busy={status.type === "loading"}
            >
              <span>{status.type === "loading" ? "Sending..." : "Submit"}</span>
              <img src={arrowCircle} alt="" aria-hidden="true" />
            </button>

            {/* Помилки */}
            {status.type === "error" && (
              <p className="form-status form-status--err" role="status">{status.msg}</p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
